import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { colors, css, fonts, glyphs, useMediaQuery } from "../../Styles";
import { Overlay, ProgressCircle, NewModal, LineInfoDisplay, FieldMode, Last10DaysViewer, UNIT, MainContentPaddingTypes, FeedCurveWithLabels } from "..";
import { ButtonTypes, ListStyle_Types, SelectFieldOption_Values } from "../../Definitions";
import { UndefinedOptions, useFeedcurveOptions } from "../_Components";
import { Graph_QL, useAnimal, MaintainAnimal, useFeedCurves, AnimalInfo } from "../../Services";
import { debounce, isEqual } from "lodash";
import store, { RootState, useAppDispatch, useAppSelector } from "../../Store";
import { selectAnimalById } from "../../Services/Agrisys_GqlDataCaller/AnimalDataCalls/AnimalDataSlices/animalsSlice";
import { selectFeedCurveById } from "../../Services/Agrisys_GqlDataCaller/FeedDataCalls/FeedDataSlices/feedCurvesSlice";
import { addActiveAnimals, clearActiveAnimals, resetActiveAnimals, selectActiveAnimalById, updateActiveAnimal } from "../../Store/activeStateHandlers/activeAnimalSlice";
import { Dispatch, UnknownAction } from "redux";
import NewButton, { NewButtonList } from "../BaseComponents/newButton";
import { CircleTypes } from "../ProgressViewer/_ProgressViewer/progressDefinitions";
import { MemoProgressCircle } from "../ProgressViewer/progressCircle";
import { ActiveEdits } from "../../Containers/FullViewScreens/GlobalScreens/Animals/animal";
import { useDispatch, useSelector } from "react-redux";
import { SET_EDIT_MODE } from "../../Store/UI/editModeSlice";
// import { EDIT_MODE, NOT_EDIT_MODE } from "../../Store/UI/editModeSlice";
import { selectMilestoneByFeedCurve } from "../../Store/optionsHandlers/optionsCombinedReducer";

interface Feed_Props {
  onEditFeedView: () => void;
  selectedAnimal: number;
  connect: {
    domain: string;
    connection: Graph_QL.Connection;
  }
}

const MAGIC_NUMBER = "15px" as const;
const unitKG = "kg" as const;

const MAGIC_GLYPH = <glyphs.Kg size={MAGIC_NUMBER} fillColor={colors.greyPrimary}/>
const MAGIC_UNIT = <UNIT content={unitKG} color={colors.greyPrimary} glyph={MAGIC_GLYPH}/>

const HAS_EXTENSION: {hasExtension?: boolean|undefined} = {hasExtension: true}

const debounceAnimalUpdate = debounce((key: keyof AnimalInfo, value: number, animal: AnimalInfo, dispatch: Dispatch<UnknownAction>) => {
  dispatch(updateActiveAnimal({id: animal.animal_id, changes: {...animal, [key]: value }}));
}, 500)
/**
 * Feed
 * @param selectedAnimal - The ID of the selected animal.
 * @param connect - Connection and domain information for data fetching.
 * @returns
 */
export default function Feed({ selectedAnimal, connect, onEditFeedView }: Feed_Props) {
  const { t } = useTranslation();
  const [feedViewContent, setFeedViewContent] = useState<"FEEDPLAN" | "LASTDAYS" | null>(null);
  const editMode = useSelector((state: RootState) => state.uiState.editMode.editMode && state.uiState.editMode.editType === ActiveEdits.viewer);
  const [feed, setFeed] = useState<number | undefined>(undefined);
  const { maintainEntries: updateAnimals } = useAnimal(connect.connection);
  const dirtRef = useRef<MaintainAnimal>({ animal_id: selectedAnimal });
  const dispatch = useAppDispatch();
  const { feedCurveOptions } = useFeedcurveOptions(UndefinedOptions.undefined)
  const dispatchEditMode = useDispatch();
  
  // #region | Mediaqueries
    const verticalFeedView = useMediaQuery(`(max-width: 1390px)`);
  // #endregion

  // Update animal_id when selectedAnimal changes
  useEffect(() => {
    dirtRef.current.animal_id = selectedAnimal;
    dispatch(clearActiveAnimals())
  }, [dispatch, selectedAnimal]);

  const animal = useAppSelector((state) => selectAnimalById(state, selectedAnimal));
  
  // #region | Data
  // Get animal from selectedAnimal
  const thisAnimal = useAppSelector((state) => {return selectActiveAnimalById(state, selectedAnimal)})

  useEffect(() => {
    if (animal) {
      dispatch(addActiveAnimals([animal]))
    }
  },[animal, dispatch])

  const animalFeedCurve = useAppSelector((state) => {if (thisAnimal?.feedcurve_id) return selectFeedCurveById(state, thisAnimal.feedcurve_id)} );
  // Get animal info from thisAnimal

  const consumptionInfo = useMemo(() => {
    if (thisAnimal?.consumptions === undefined) {
      return
    }
    const consumptionToday = thisAnimal.consumptions ? thisAnimal.consumptions.find(e => e.milestoneDayOffset!==undefined && e.milestoneDayOffset === 0):undefined;
    const consumptionYesterday = thisAnimal.consumptions ? thisAnimal.consumptions.find(e => e.milestoneDayOffset!==undefined && e.milestoneDayOffset === 1):undefined;
    return {consumptionToday, consumptionYesterday}
  },[thisAnimal?.consumptions])

  const animalInfo = useMemo(() => {
    if (!thisAnimal) return;

    // Feed Correction
    const feedCorrection = thisAnimal.correction === undefined || thisAnimal.correction === null
      ? "0%"
      : `${thisAnimal.correction}%`;
    
  
    // Milestone
    const milestone = thisAnimal.milestone
     const milestone_day = thisAnimal.milestone_day

    return {
      feedCorrection,
      feedCurve: animalFeedCurve,
      milestone,
      milestone_day
    };
  }, [animalFeedCurve, thisAnimal]);
  // #endregion

  const milestoneOptions = useAppSelector((state) => {if (thisAnimal?.feedcurve_id !== undefined) {return selectMilestoneByFeedCurve(state, [thisAnimal.feedcurve_id])}}, isEqual)
  // #endregion

  // Set feed correction
  useEffect(() => {
    setFeed(thisAnimal?.correction)
  }, [thisAnimal?.correction]);

  // #region | Line Displays
  // Feed Plan
  const info_feedPlan = (
    <LineInfoDisplay
      editMode={editMode}
      gridTemplateColumns={"1fr 25px 1fr"}
      value={thisAnimal?.feedCurve?.name}
      undefinedValue={t("negativeParams.feedCurve", { defaultValue: "No Feed Curve" })}
      header={t("feed.feedCurve.feedCurve", { defaultValue: "Feed Curve" })}
      icon={"FeedCurve"}
      fieldMode={FieldMode.select}
      options={feedCurveOptions ?? []}
      onchange={(option: SelectFieldOption_Values | undefined) => {
        dirtRef.current.feedcurve_id = option?.id ? Number(option?.id): NaN
        if (isNaN(Number(option?.id))) {
          dispatch(updateActiveAnimal({id: thisAnimal.animal_id, changes: {...thisAnimal, feedcurve_id: Number(option?.id)}}))
          return;
        }

        const feedCurve = selectFeedCurveById(store.getState(), Number(option?.id)) ?? undefined;
        if (feedCurve === undefined) {
          dispatch(updateActiveAnimal({id: thisAnimal.animal_id, changes: {...thisAnimal, feedcurve_id: Number(option?.id)}}))
          return;
        }
         
        const milestone = feedCurve.milestones?.find((milestone) => milestone.milestoneId === thisAnimal.milestone?.milestone_id);
        if (milestone !== undefined) {
          dispatch(updateActiveAnimal({id: thisAnimal.animal_id, changes: {...thisAnimal, feedcurve_id: Number(option?.id), feedcurve_milestones: milestone.feedCurveMilestoneId}}))
          dirtRef.current.fk_feedcurve_milestones = milestone.feedCurveMilestoneId
          if (thisAnimal.milestone_day) {
            dirtRef.current.milestone_day = Math.floor(thisAnimal.milestone_day/1000);
          }
          return;
        }

        const newMilestone = feedCurve.milestones?.at(0);
        dispatch(updateActiveAnimal({id: thisAnimal.animal_id, changes: {...thisAnimal, feedcurve_id: Number(option?.id), feedcurve_milestones: newMilestone?.feedCurveMilestoneId, milestone: newMilestone ? {milestone_id: newMilestone.milestoneId, name: newMilestone.name} : newMilestone, milestone_day: Date.now()}}))
        dirtRef.current.fk_feedcurve_milestones = newMilestone ? newMilestone.feedCurveMilestoneId : NaN
        dirtRef.current.milestone_day = Math.floor(Date.now()/1000);
      }}
      defaultValue={feedCurveOptions?.find(({ id }) => thisAnimal&&thisAnimal.feedCurve?.feedCurveId === id) ?? undefined}
      isClearable
    />
  );

  // Feed Correction
  const info_corrections = (
    <LineInfoDisplay
      editMode={editMode}
      gridTemplateColumns={"1fr 25px 1fr"}
      value={animalInfo ? animalInfo.feedCorrection ?? "0%" : "0%"}
      header={t("feed.feedCorrection.feedCorrection", { defaultValue: "Feed Corrections" })}
      icon={"FeedCorrection"}
      fieldMode={FieldMode.feedCorrection}
      onChange={(e: number) => { dirtRef.current.correction = Number(e); setFeed(e); debounceAnimalUpdate("correction", Number(e), thisAnimal, dispatch) }}
      defaultValue={feed}
    />
  );

  // Milestone
  const info_milestone = (
    <LineInfoDisplay
      editMode={editMode}
      gridTemplateColumns={"1fr 25px 1fr"}
      value={thisAnimal ? thisAnimal.milestone?.name : ""}
      header={t("feed.feedCorrection.milestone", { defaultValue: "Milestone" })}
      undefinedValue={t("neagtiveParams.milestone", { defaultValue: "No Milestone" })}
      icon={"Milestone"}
      fieldMode={FieldMode.select}
      options={milestoneOptions ?? []}
      onchange={(option: SelectFieldOption_Values | undefined) => {(dirtRef.current.fk_feedcurve_milestones = option?.id ? Number(option.id) : NaN); dispatch(updateActiveAnimal({id: thisAnimal.animal_id, changes: {...thisAnimal, feedcurve_milestones: Number(option?.id)}}))}}
      defaultValue={milestoneOptions?.find(({milestoneId}) => thisAnimal&&thisAnimal.milestone?.milestone_id === milestoneId) ?? undefined}
      isClearable
    />
  );

  const milestoneDayCallback = useCallback(() => {
    const animal = dirtRef.current
    if (animal && animal.milestone_day && animal.milestone_day !== thisAnimal.milestone_day) {
      dispatch(updateActiveAnimal({id: animal.animal_id, changes: {...animal, responder_number: animal.responder_number ?? undefined, milestone_day: animal.milestone_day*1000}}))
    }
  },[dispatch, thisAnimal?.milestone_day])
  
  // Milestone
  const info_milestone_day = (
    <LineInfoDisplay 
      gridTemplateColumns={"1fr 25px 1fr"}
      editMode={editMode}
      value={thisAnimal?.milestone_day ? Math.floor((Date.now() - thisAnimal?.milestone_day) / (1000 * 60 * 60 * 24)) : undefined}
      header={t("params.milestoneDay", { defaultValue: "Milestone Day" })}
      undefinedValue={t("negativeParams.milestoneDay", { defaultValue: "No Milestone Day" })}
      icon={"MilestoneDay"}
      fieldMode={FieldMode.input}
      onBlur={milestoneDayCallback}
      onchange={(e: string|number) => (dirtRef.current.milestone_day = Math.round((Date.now() - Number(e) * 86400000) / 1000) ? Math.round((Date.now() - Number(e) * 86400000) / 1000) : NaN)}
      defaultValue={thisAnimal?.milestone_day ? Math.floor((Date.now() - thisAnimal?.milestone_day) / (1000 * 60 * 60 * 24)) : undefined}
      isClearable
      type='number'
    />
  );
  // #endregion

  // #region | Feed graphs
  // Feed Graphs Buttons
  const feedGraphsBtns = (
    <NewButtonList
      contents={[
        t("feed.feedCurve.feedCurve", { defaultValue: "feed curve" }),
        t("feed.general.feedLastDays", { defaultValue: "last 10 days" }),
      ]}
      overrides={{ baseStyles: { width: "fit-content" } }}
      onClick={(i) => {
        if (i === 0) { setFeedViewContent("FEEDPLAN") }
        if (i === 1) { setFeedViewContent("LASTDAYS") }
      }}
      listOverrides={{ listStyle: ListStyle_Types.horizontal }}
    />
  );

  // Feed Curve Graph
  const feedCurve = ( <>
    {animalFeedCurve ? (
      <FeedCurveWithLabels data={animalFeedCurve} />
    ) : (
      <p style={{ ...fonts.paragraph, ...fonts.fontSize4, color: colors.greyDarkPrimary }}> {t("feed.feedCurve.noFeedCurve", { defaultValue: "No Feed Curve Found" })} </p>
    )}
  </>);
  
  // Last 10 Days Graph
  const lastDays = (<>
    {thisAnimal?.lastTenDay ? (
      <div style={{ marginTop: "20px", display: "flex", alignItems: "center", justifyContent: "center", marginBottom: "20px" }}>
        <Last10DaysViewer data={thisAnimal.lastTenDay} />
      </div>
    ) : (
      <p style={{ ...fonts.paragraph, ...fonts.fontSize4, color: colors.greyDarkPrimary }}> {t("feed.feedCurve.noConsumptions", { defaultValue: "No Consumptions Data Found" })} </p>
    )} </>);
  
  // Feed View Modal
  const feedGraphsModals = (
    feedViewContent !== null &&
    <NewModal
      overrides={{
        backgroundColor: colors.greyLightTertiary,
        mainContentScroll: true,
        mainContentPadding: MainContentPaddingTypes.primary
      }}
      tabButtons={{
        contents: [
          t("feed.feedCurve.feedCurve", { defaultValue: "feed curve" }),
          t("feed.general.feedLastDays", { defaultValue: "last 10 days" })
        ],
        onClick: (i) => {
          if (i === 0) { setFeedViewContent("FEEDPLAN") }
          if (i === 1) { setFeedViewContent("LASTDAYS") }
        },
        startEnhancers: [glyphs.FeedCurve({}), glyphs.FeedPlan({})],
        disabled: feedViewContent === "FEEDPLAN" ? 0 : 1,
        active: feedViewContent === "FEEDPLAN" ? 0 : 1
      }}
      predefinedButtons={{ onClose: () => { setFeedViewContent(null) }}}
      header={ feedViewContent === "FEEDPLAN"
        ? t("feed.feedCurve.feedCurve", { defaultValue: "feed curve" })
        : t("feed.general.feedLastDays", { defaultValue: "last 10 days" })
      }
      subHeader={`${t("animal.animalId", { defaultValue: "animal no." })} ${thisAnimal?.animal_number}`}
      mainContent={
        <div style={{display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: "20px"}}>
          { feedViewContent === "FEEDPLAN"
            ? <>{feedCurve}</>
            : <>{lastDays}</>
          }
        </div>
      }
    />
  );
  // #endregion
  
  // Sending just the translation key to the component could be considered and then let the component handle the render which would remove this memo.
  const labelToday = useMemo(() => (
    {
      header: t("feed.general.remainingToday", { defaultValue: "remaining today" }),
      unit: MAGIC_UNIT
    }
  ),[t])

  const labelYesterday = useMemo(() => (
    {
      header: t("feed.general.remainingYesterday", { defaultValue: "remaining yesterday" }),
      unit: MAGIC_UNIT 
    }
  ),[t])

  return ( <>
    <div style={{ height: "100%" }}>
      
      <div style={{ display: "flex", justifyContent: "end", padding: "0px 20px" }}>
        {editMode ? (
          <NewButtonList
            contents={[
              t("buttons.save", { defaultValue: "save" }),
              t("buttons.cancel", { defaultValue: "cancel" }),
              t("buttons.edit", { defaultValue: "edit" }),
            ]}
            buttonType={ButtonTypes.tableButton}
            startEnhancers={[
              glyphs.Check({}),
              glyphs.Close({}),
              glyphs.Edit({}),
            ]}
            onClick={(i) => {
              if (i === 0) { dispatchEditMode(SET_EDIT_MODE({ enabled: false, type: undefined })); if (updateAnimals) { updateAnimals({ data: [dirtRef.current] })} dirtRef.current = { animal_id: selectedAnimal } }
              if (i === 1) { dispatchEditMode(SET_EDIT_MODE({ enabled: false, type: undefined })); dirtRef.current = { animal_id: selectedAnimal }; dispatch(resetActiveAnimals([animal])) }
            }}
            disabled={[false, false, true]}
            overrides={(index) => ({
              baseStyles: { backgroundColor: index === 2 ? colors.greyPrimary : colors.greySecondary },
              hoverStyles: { backgroundColor: colors.greyPrimary },
            })}
          />
        ) : (
          <NewButton
            content={t("buttons.edit", { defaultValue: "edit" })}
            buttonType={ButtonTypes.tableButton}
            startEnhancer={glyphs.Edit({})}
            onClick={() => onEditFeedView()}
          />
        )}
      </div>

      <div style={{ backgroundColor: colors.greyPrimary, border: "5px solid " + colors.greyPrimary, borderRadius: "10px", display: "grid", gridAutoColumns: verticalFeedView ? "1fr" : "320px 1fr", gridAutoRows: verticalFeedView ? "200px 1fr" : "1fr", boxSizing: "border-box", overflow: "hidden", height: "calc(100% - 50px)" }}>

          <div style={{ gridArea: "1/1/2/2", boxSizing: "border-box", display: "flex", alignItems: "center", justifyContent: "center", padding: "20px 0px", backgroundColor: colors.greyLightTertiary }}>
            {/* Fed Today */}
            <MemoProgressCircle
              total={consumptionInfo?.consumptionToday?.total}
              progressed={consumptionInfo?.consumptionToday?.progressed}
              animation
              circleType={CircleTypes.feed}
              labels={labelToday}
              extension={HAS_EXTENSION}
            />
            {/* Fed During Period */}
            <MemoProgressCircle
              total={consumptionInfo?.consumptionYesterday?.total}
              progressed={consumptionInfo?.consumptionYesterday?.progressed}
              animation
              circleType={CircleTypes.feed}
              labels={labelYesterday}
              extension={HAS_EXTENSION 
              }
            />
          </div>

        <div style={{ gridArea: verticalFeedView ? "2/1/3/2" : "1/2/2/3", padding: "20px 0px", display: "flex", gap: "20px", flexDirection: "column", alignItems: "center", justifyContent: "space-between", overflowY: "auto", borderTop: "5px solid "+colors.greyPrimary, boxSizing: "border-box" }}>
          <div style={{ width: "300px", display: "flex", flexDirection: "column", justifyContent: "center", alignItems: "center", gap: "10px" }}>
            {info_feedPlan}
            {info_corrections}
            {info_milestone}
            {info_milestone_day}
          </div>

          <div style={{display: "flex", flexDirection: "column", gap: "10px", width: "100%", alignItems: "center"}}>
            <div style={{ borderBottom: "2px solid " + colors.greySecondary, width: "90%"}} />
            {feedGraphsBtns}
          </div>
        </div>
      </div>

    </div>

    {feedGraphsModals}
  </> );
}
